import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Download, X } from "lucide-react";

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

const fmtSize = (b) => { if (!b) return ""; if (b < 1024) return "<1 Ko"; const k = b / 1024; return k < 1024 ? `${Math.round(k)} Ko` : `${(k / 1024).toFixed(1)} Mo`; };

export default function AssocDocuments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/associations/me/documents"); setItems(data.items || []); }
    catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choisissez un fichier."); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 15 Mo)."); return; }
    if (!title.trim()) { toast.error("Donnez un titre au document."); return; }
    setBusy(true);
    try {
      const content_base64 = await fileToDataUrl(f);
      await api.post("/associations/me/documents", { title: title.trim(), filename: f.name, content_base64, content_type: f.type || "application/octet-stream" });
      toast.success("Document ajouté.");
      setTitle(""); setAdding(false); if (fileRef.current) fileRef.current.value = ""; load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible."); }
    finally { setBusy(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Supprimer « ${d.title} » ?`)) return;
    try { await api.delete(`/associations/me/documents/${d.id}`); toast.success("Document supprimé."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible."); }
  };

  return (
    <div className="max-w-4xl" data-testid="assoc-documents">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold">Mes documents</h1>
          <p className="text-slate-500 text-sm mt-1">Statuts, PV d'assemblée générale, règlement intérieur… conservés au même endroit.</p>
        </div>
        <Button onClick={() => setAdding(true)} className="btn-shine text-white" data-testid="assoc-doc-add-btn"><Plus size={16} className="mr-1" /> Ajouter un document</Button>
      </div>

      {adding && (
        <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="assoc-doc-form">
          <div className="flex items-center justify-between"><h2 className="font-heading font-bold">Nouveau document</h2><button onClick={() => setAdding(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
          <div><Label>Titre *</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="ex : Statuts 2026" data-testid="assoc-doc-title" /></div>
          <div><Label>Fichier * <span className="text-slate-400 font-normal">(PDF, Word, image… max 15 Mo)</span></Label><input ref={fileRef} type="file" className="block text-sm mt-1" data-testid="assoc-doc-file" /></div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setAdding(false)}>Annuler</Button><Button onClick={upload} disabled={busy} className="btn-shine text-white" data-testid="assoc-doc-save">{busy ? "Envoi…" : "Enregistrer"}</Button></div>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {loading ? <div className="text-slate-500 text-sm">Chargement…</div> : items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center text-slate-500" data-testid="assoc-documents-empty">
            <FileText size={30} className="mx-auto text-slate-300 mb-2" /> Aucun document pour l'instant.
          </div>
        ) : items.map((d) => (
          <div key={d.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4" data-testid={`assoc-doc-row-${d.id}`}>
            <div className="w-12 h-12 rounded-xl bg-emerald-50 grid place-items-center shrink-0"><FileText className="text-emerald-700" size={20} /></div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 truncate">{d.title}</div>
              <div className="text-xs text-slate-500 truncate">{d.filename} {d.size ? `· ${fmtSize(d.size)}` : ""}</div>
            </div>
            <div className="flex gap-1 shrink-0">
              <a href={d.file_url} target="_blank" rel="noopener noreferrer" download className="p-2 text-slate-400 hover:text-emerald-700" title="Télécharger" data-testid={`assoc-doc-download-${d.id}`}><Download size={16} /></a>
              <Button variant="ghost" size="sm" onClick={() => remove(d)} className="text-rose-600 hover:text-rose-700" data-testid={`assoc-doc-delete-${d.id}`}><Trash2 size={15} /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
